import type { Preset } from '../../api/types.js';
import type { TourSnapshot } from '../../tour/types.js';
import type { ComponentInstance } from '../component.js';
import { setHtmlIcon, setText } from '../dom-helpers.js';
import { ICON_TOUR_PAUSE, ICON_TOUR_PLAY } from '../icons.js';

export interface PresetPillProps {
  presets: readonly Preset[];
  tourSnapshot: TourSnapshot;
  embed: boolean;
}

export interface PresetPillCallbacks {
  onApplyPreset: (presetId: string) => void;
  onTourStart: () => void;
  onTourPauseResume: () => void;
}

interface ButtonRefs {
  presets: HTMLButtonElement[];
  tour: HTMLButtonElement;
  tourLabel: HTMLSpanElement;
  tourIconWrap: HTMLSpanElement;
  progressWrap: HTMLDivElement;
  progressTrack: HTMLDivElement;
  progressBar: HTMLDivElement;
  progressLabel: HTMLParagraphElement;
}

const BTN_BASE =
  'padding:0.375rem 0.75rem;border-radius:9999px;background:transparent;color:#d4d4d8;border:0;font-family:system-ui,sans-serif;font-size:0.6875rem;cursor:pointer;transition:background 100ms';

function styleTourButton(btn: HTMLButtonElement, snap: TourSnapshot): void {
  const active = snap.kind !== 'idle';
  btn.style.cssText = `${BTN_BASE};display:flex;align-items:center;gap:0.375rem;background:${
    active ? 'rgba(168,85,247,0.30)' : 'rgba(168,85,247,0.15)'
  };color:${active ? '#e9d5ff' : '#d8b4fe'}`;
}

function stylePresetButton(btn: HTMLButtonElement, snap: TourSnapshot, presetIdx: number): void {
  const isCurrent = snap.kind !== 'idle' && snap.index === presetIdx;
  const isVisited = snap.visited.includes(presetIdx);
  const base = `${BTN_BASE};display:inline-flex;align-items:center;gap:0.25rem`;
  if (isCurrent) {
    btn.style.cssText = `${base};background:rgba(255,255,255,0.15);color:#ffffff;box-shadow:inset 0 0 0 1px rgba(168,85,247,0.40)`;
  } else if (isVisited) {
    btn.style.cssText = `${base};color:#a1a1aa`;
  } else {
    btn.style.cssText = base;
  }
}

function tourLabelText(snap: TourSnapshot): string {
  if (snap.kind === 'idle') return 'Tour';
  if (snap.kind === 'paused') return 'Riprendi';
  return 'Pausa';
}

function tourAriaLabel(snap: TourSnapshot): string {
  if (snap.kind === 'idle') return 'Avvia tour guidato';
  if (snap.kind === 'paused') return 'Riprendi tour guidato';
  return 'Pausa tour guidato';
}

export function mountPresetPill(
  root: HTMLElement,
  initial: PresetPillProps,
  callbacks: PresetPillCallbacks,
): ComponentInstance<PresetPillProps> {
  // No presets → no UI; return a no-op instance.
  if (initial.presets.length === 0) {
    return {
      update() {
        /* no-op */
      },
      cleanup() {
        /* no-op */
      },
    };
  }

  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'group');
  wrap.setAttribute('aria-label', 'Camera preset e tour guidato');
  wrap.style.cssText = initial.embed
    ? 'position:fixed;bottom:0.75rem;left:50%;transform:translateX(-50%);z-index:11;pointer-events:auto'
    : 'position:fixed;bottom:3rem;left:50%;transform:translateX(-50%);z-index:11;pointer-events:auto';

  const pill = document.createElement('div');
  pill.style.cssText =
    'background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.08);padding:0.25rem 0.375rem;border-radius:9999px;display:flex;align-items:center;gap:0.25rem';
  wrap.appendChild(pill);

  // Tour button.
  const tour = document.createElement('button');
  tour.type = 'button';
  tour.classList.add('viewer-ui-tap-target');
  tour.setAttribute('aria-label', tourAriaLabel(initial.tourSnapshot));
  const tourIconWrap = document.createElement('span');
  tourIconWrap.style.cssText = 'display:inline-flex;align-items:center';
  tour.appendChild(tourIconWrap);
  const tourLabel = document.createElement('span');
  tour.appendChild(tourLabel);
  styleTourButton(tour, initial.tourSnapshot);
  setHtmlIcon(
    tourIconWrap,
    initial.tourSnapshot.kind === 'idle' || initial.tourSnapshot.kind === 'paused'
      ? ICON_TOUR_PLAY
      : ICON_TOUR_PAUSE,
  );
  setText(tourLabel, tourLabelText(initial.tourSnapshot));
  tour.addEventListener('click', () => {
    const isIdle = currentSnap.kind === 'idle';
    if (isIdle) {
      callbacks.onTourStart();
    } else {
      callbacks.onTourPauseResume();
    }
  });
  pill.appendChild(tour);

  // Separator.
  const sep = document.createElement('span');
  sep.setAttribute('aria-hidden', 'true');
  sep.style.cssText = 'width:1px;height:1rem;background:rgba(255,255,255,0.10);margin:0 0.25rem';
  pill.appendChild(sep);

  // Per-preset buttons.
  const presetBtns: HTMLButtonElement[] = [];
  initial.presets.forEach((p, idx) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.classList.add('viewer-ui-tap-target');
    b.dataset['presetId'] = p.id;
    setText(b, p.label);
    stylePresetButton(b, initial.tourSnapshot, idx);
    b.setAttribute('aria-label', `Vai a ${p.label}`);
    if (initial.tourSnapshot.kind !== 'idle' && initial.tourSnapshot.index === idx) {
      b.setAttribute('aria-current', 'true');
    }
    if (initial.tourSnapshot.visited.includes(idx)) {
      b.setAttribute('aria-pressed', 'true');
    }
    b.addEventListener('mouseenter', () => {
      if (currentSnap.kind === 'idle' || currentSnap.index !== idx) {
        b.style.backgroundColor = 'rgba(255,255,255,0.10)';
      }
    });
    b.addEventListener('mouseleave', () => {
      stylePresetButton(b, currentSnap, idx);
    });
    b.addEventListener('click', () => {
      callbacks.onApplyPreset(p.id);
    });
    pill.appendChild(b);
    presetBtns.push(b);
  });

  // Progress wrapper (visible only when dwelling/paused).
  const progressWrap = document.createElement('div');
  progressWrap.style.cssText = 'margin:0.5rem 0.75rem 0';
  progressWrap.style.display = 'none';

  const progressTrack = document.createElement('div');
  progressTrack.setAttribute('role', 'progressbar');
  progressTrack.setAttribute('aria-label', 'Countdown prossima vista');
  progressTrack.setAttribute('aria-valuemin', '0');
  progressTrack.setAttribute('aria-valuemax', '100');
  progressTrack.style.cssText =
    'height:2px;background:rgba(255,255,255,0.08);border-radius:9999px;overflow:hidden';

  const progressBar = document.createElement('div');
  progressBar.style.cssText =
    'height:100%;background:#c084fc;border-radius:9999px;width:0%;transition:width 200ms linear';
  progressTrack.appendChild(progressBar);

  const progressLabel = document.createElement('p');
  progressLabel.style.cssText =
    'margin:0.25rem 0 0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.625rem;color:#a1a1aa;text-align:center';
  progressLabel.textContent = '';

  progressWrap.appendChild(progressTrack);
  progressWrap.appendChild(progressLabel);
  wrap.appendChild(progressWrap);

  root.appendChild(wrap);

  let currentSnap: TourSnapshot = initial.tourSnapshot;
  const currentPresetCount = initial.presets.length;

  const refs: ButtonRefs = {
    presets: presetBtns,
    tour,
    tourLabel,
    tourIconWrap,
    progressWrap,
    progressTrack,
    progressBar,
    progressLabel,
  };

  const applyTourSnapshot = (snap: TourSnapshot): void => {
    currentSnap = snap;
    styleTourButton(refs.tour, snap);
    setHtmlIcon(
      refs.tourIconWrap,
      snap.kind === 'idle' || snap.kind === 'paused' ? ICON_TOUR_PLAY : ICON_TOUR_PAUSE,
    );
    setText(refs.tourLabel, tourLabelText(snap));
    refs.tour.setAttribute('aria-label', tourAriaLabel(snap));

    refs.presets.forEach((btn, idx) => {
      stylePresetButton(btn, snap, idx);
      if (snap.kind !== 'idle' && snap.index === idx) {
        btn.setAttribute('aria-current', 'true');
      } else {
        btn.removeAttribute('aria-current');
      }
      if (snap.visited.includes(idx)) {
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.removeAttribute('aria-pressed');
      }
    });

    const showProgress = snap.kind === 'dwelling' || snap.kind === 'paused';
    refs.progressWrap.style.display = showProgress ? 'block' : 'none';
    if (showProgress) {
      const totalMs = 6000; // DWELL_MS — local copy to avoid importing into UI
      const pct = Math.max(0, Math.min(100, ((totalMs - snap.remainingMs) / totalMs) * 100));
      refs.progressBar.style.width = `${pct.toFixed(1)}%`;
      // aria-valuenow MUST be on the element with role="progressbar" for AT
      // to announce progress (the inner bar div is just visual).
      refs.progressTrack.setAttribute('aria-valuenow', pct.toFixed(0));
      const seconds = Math.max(0, Math.ceil(snap.remainingMs / 1000));
      refs.progressLabel.textContent =
        snap.kind === 'paused' ? 'tour in pausa' : `prossima vista in ${seconds}s`;
    }
  };

  // Initial paint.
  applyTourSnapshot(initial.tourSnapshot);

  return {
    update(props: PresetPillProps) {
      // Presets list change (e.g. RETRY new metadata) is not handled in 4.F:
      // SCENE_LOADED happens once per loaded state. Defensive guard: warn if
      // length changes — caller should remount.
      if (props.presets.length !== currentPresetCount) {
        console.warn('[preset-pill] presets length changed; remount required');
      }
      applyTourSnapshot(props.tourSnapshot);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
